import { get, post, put, del } from "@/shared/utils/fetchApi";
import { SettingsService } from "@/shared/services/api/SettingsService";
import {
  WorkspaceOverviewBlock,
  CreateWorkspaceOverviewBlockDTO,
  UpdateWorkspaceOverviewBlockDTO,
  ReorderBlocksDTO,
  BlockContent,
} from "../types/WorkspaceOverviewTypes";

export interface OverviewTemplate {
  key: string;
  label: { en: string; pt: string };
  description: { en: string; pt: string };
  prompt: { en: string; pt: string };
}

export interface OverviewTemplatesConfig {
  version: string;
  templates: OverviewTemplate[];
}

export class WorkspaceOverviewService {
  static async getBlocksByWorkspaceId(
    workspaceId: string,
    filters?: { is_active?: boolean; block_type?: string },
  ): Promise<WorkspaceOverviewBlock[]> {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) {
      params.append("is_active", String(filters.is_active));
    }
    if (filters?.block_type) {
      params.append("block_type", filters.block_type);
    }

    const queryString = params.toString();
    const url = `/products/${workspaceId}/overview/blocks${queryString ? `?${queryString}` : ""}`;

    return get<WorkspaceOverviewBlock[]>(url);
  }

  static async getOverviewAsObject(
    workspaceId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<Record<string, any>> {
    const url = `/products/${workspaceId}/overview`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return get<Record<string, any>>(url);
  }

  static async getBlockById(id: string): Promise<WorkspaceOverviewBlock> {
    const url = `/products/overview/blocks/${id}`;

    return get<WorkspaceOverviewBlock>(url);
  }

  static async createBlock(
    block: CreateWorkspaceOverviewBlockDTO,
  ): Promise<WorkspaceOverviewBlock> {
    const url = `/products/${block.product_id}/overview/blocks`;

    return post<WorkspaceOverviewBlock>(url, block);
  }

  static async updateBlock(
    id: string,
    block: UpdateWorkspaceOverviewBlockDTO,
  ): Promise<WorkspaceOverviewBlock> {
    const url = `/products/overview/blocks/${id}`;

    return put<WorkspaceOverviewBlock>(url, block);
  }

  static async deleteBlock(id: string): Promise<void> {
    const url = `/products/overview/blocks/${id}`;

    return del<void>(url);
  }

  static async reorderBlocks(
    workspaceId: string,
    reorderData: ReorderBlocksDTO,
  ): Promise<WorkspaceOverviewBlock[]> {
    const url = `/products/${workspaceId}/overview/blocks/reorder`;

    return post<WorkspaceOverviewBlock[]>(url, reorderData);
  }

  static async generateBlocks(
    workspaceId: string,
    language?: string,
    preview?: boolean,
    customPrompt?: string,
    maxTokens?: number,
    temperature?: number,
  ): Promise<WorkspaceOverviewBlock[]> {
    const url = `/products/${workspaceId}/overview/generate`;
    const body = { language, preview, customPrompt, maxTokens, temperature };

    return post<WorkspaceOverviewBlock[]>(url, body);
  }

  // Sem max_tokens/temperature: /overview/generate-content usa os limites do
  // proprio backend (diferente de /overview/generate, que aceita os dois).
  static async generateBlockContent(
    workspaceId: string,
    blockType: string,
    title: string,
    description?: string,
    language?: string,
  ): Promise<{ content: BlockContent }> {
    const url = `/products/${workspaceId}/overview/generate-content`;
    const body = { block_type: blockType, title, description, language };

    return post<{ content: BlockContent }>(url, body);
  }

  static async syncAllBlocksToKnowledgeBase(
    workspaceId: string,
  ): Promise<{ success: boolean; count: number; message?: string }> {
    const url = `/products/${workspaceId}/overview/sync-to-kb`;

    return post<{ success: boolean; count: number; message?: string }>(url);
  }

  static async clearOverviewEmbeddings(
    workspaceId: string,
  ): Promise<{ success: boolean; count: number; error?: string }> {
    const url = `/products/${workspaceId}/overview/embeddings`;

    return del<{ success: boolean; count: number; error?: string }>(url);
  }

  private static async fetchTemplates(
    settingKey: string,
  ): Promise<OverviewTemplate[]> {
    const config =
      await SettingsService.getSettingByType<OverviewTemplatesConfig>(
        settingKey,
      );
    return config?.templates ?? [];
  }

  static async getOverviewTemplates(): Promise<OverviewTemplate[]> {
    return WorkspaceOverviewService.fetchTemplates(
      "overview_generation_templates",
    );
  }

  static async getBlockTemplates(): Promise<OverviewTemplate[]> {
    return WorkspaceOverviewService.fetchTemplates(
      "block_generation_templates",
    );
  }
}
